import * as React from 'react';

import { useFetchDataForIri } from 'bones/components/molecules/Voicebox/context/panelData/useFetchDataForIri';
import { useRequestAnalysis } from 'bones/components/molecules/Voicebox/context/panelData/useRequestAnalysis';
import { useRequestChart } from 'bones/components/molecules/Voicebox/context/panelData/useRequestChart';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxConversationContextValue } from 'bones/constants/voicebox/conversation';
import { VoiceboxResponseMessageType } from 'bones/constants/voicebox/message';
import {
  VoiceboxPanelDataContextValue,
  VoiceboxPanelHistory,
} from 'bones/constants/voicebox/panel';
import { VoiceboxSchema } from 'bones/constants/voicebox/schema';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { getLocalName } from 'bones/utils/getLocalName';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { isEqual } from 'bones/utils/isEqual';
import { noop } from 'bones/utils/noop';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';

export const DEFAULT_VOICEBOX_PANEL_DATA_CONTEXT_VALUE: VoiceboxPanelDataContextValue =
  {
    addPanelHistory: noop,
    currentPanel: null,
    panelHistory: [],
    setPanelHistory: noop,

    analysisDataByMessageId: {},
    requestAnalysis: noop,
    setAnalysis: noop,

    chartDataByMessageId: {},
    requestChart: noop,
    setChartCustomizations: noop,

    dataByIri: {},
    getLabelOrLocalName: /* istanbul ignore next */ () => '',
    labels: {
      labelsByIri: {},
      loading: false,
    },
    provenance: {
      graphsByType: {},
      metadata: {
        dataSourcesByIri: {},
        schemasByIri: {},
        tablesByIri: {},
        columnsByIri: {},
      },
      loading: false,
    },
  };

export const VoiceboxPanelDataContext =
  React.createContext<VoiceboxPanelDataContextValue>(
    DEFAULT_VOICEBOX_PANEL_DATA_CONTEXT_VALUE
  );

export const useVoiceboxPanelData = () => {
  return React.useContext(VoiceboxPanelDataContext);
};

export const useVoiceboxPanelDataValue = ({
  id,
  initialPanelHistory = null,
  limit = 10,
  conversation: { conversationId, messages },
  schema: {
    config: { catalogDatabaseId, connectionId, connectionIdx },
    copy,
  },
  settingsContext,
}: {
  /**
   * the unique key for managing multiple instances of voicebox,
   * i.e. the Studio note id
   */
  id?: string | null;
  initialPanelHistory?: VoiceboxPanelHistory[] | null;
  limit?: number;
  conversation: VoiceboxConversationContextValue;
  schema: VoiceboxSchema;
  settingsContext: VoiceboxSettingsContextValue;
}): VoiceboxPanelDataContextValue => {
  const { modelsByDatabaseId, modelsLoading, settings } = settingsContext;
  const { databaseId } = settings;

  const model = modelsByDatabaseId[settings.databaseId]?.[
    settings.model || DEFAULT_MODEL_NAME
  ] as QueryBuilderModel | undefined;

  const [panelHistory, setPanelHistory] = React.useState<
    VoiceboxPanelHistory[] | null
  >(initialPanelHistory);

  const [analysisDataByMessageId, setAnalysisDataByMessageId] = React.useState<
    VoiceboxPanelDataContextValue['analysisDataByMessageId']
  >({});

  const [chartDataByMessageId, setChartDataByMessageId] = React.useState<
    VoiceboxPanelDataContextValue['chartDataByMessageId']
  >({});

  const [dataByIri, setDataByIri] = React.useState<
    VoiceboxPanelDataContextValue['dataByIri']
  >({});

  const [labels, setLabels] = React.useState<
    VoiceboxPanelDataContextValue['labels']
  >({ labelsByIri: {}, loading: false });

  const [provenance, setProvenance] = React.useState<
    VoiceboxPanelDataContextValue['provenance']
  >({
    graphsByType: {},
    metadata: {
      dataSourcesByIri: {},
      schemasByIri: {},
      tablesByIri: {},
      columnsByIri: {},
    },
    loading: false,
  });

  const didIdChange = useDidValueChange(id);
  const didInitialPanelHistoryChange = useDidValueChange(
    initialPanelHistory,
    isEqual
  );
  const didConversationIdChange = useDidValueChange(conversationId);
  React.useEffect(() => {
    if (
      didIdChange ||
      didConversationIdChange ||
      didInitialPanelHistoryChange
    ) {
      setPanelHistory(initialPanelHistory);
    }
  }, [
    didConversationIdChange,
    didIdChange,
    didInitialPanelHistoryChange,
    initialPanelHistory,
  ]);

  const lastMessage = messages[messages.length - 1];
  const lastResponseMessage = lastMessage?.isResponse ? lastMessage : null;
  const didLastResponseMessageChange = useDidValueChange(
    lastResponseMessage,
    isEqual
  );

  React.useEffect(() => {
    if (
      didLastResponseMessageChange &&
      lastResponseMessage?.success &&
      lastResponseMessage.type === VoiceboxResponseMessageType.ANSWER
    ) {
      setPanelHistory((prev) =>
        !prev || prev.length
          ? [{ messageId: lastResponseMessage.messageId }]
          : prev
      );
    }
  }, [didLastResponseMessageChange, lastResponseMessage]);

  const currentPanel: VoiceboxPanelHistory | null = panelHistory
    ? panelHistory[panelHistory.length - 1] || null
    : null;
  const currentIri = currentPanel?.iri || null;

  const hasCurrentIriData = Boolean(currentIri && dataByIri[currentIri]);
  const didHasCurrentIriDataChange = useDidValueChange(hasCurrentIriData);

  const didCurrentIriChange = useDidValueChange(currentIri);
  const didSettingsChange = useDidValueChange(settings, isEqual);
  const didModelChange = useDidValueChange(model, isEqual);
  const didModelsLoadingChange = useDidValueChange(modelsLoading, isEqual);

  const getLabelOrLocalName = React.useCallback(
    (iri: string) => labels.labelsByIri[iri] || getLocalName(iri),
    [labels]
  );

  const fetchDataForIri = useFetchDataForIri({
    catalogDatabaseId,
    connectionIdx,
    labels,
    limit,
    model,
    provenance,
    setDataByIri,
    setLabels,
    setProvenance,
    settingsContext,
  });

  React.useEffect(() => {
    // clear (and refetch) on any kind of settings change (databaseId, reasoning, model, graph)
    if (didSettingsChange) {
      setDataByIri({});
      setLabels({ labelsByIri: {}, loading: false });
    }

    if ((didModelChange || didModelsLoadingChange) && !modelsLoading && model) {
      // pre-populate existing labels from model
      const newLabelsByIri = Object.fromEntries(
        Object.values({
          ...model.classesByIri,
          ...model.propertiesByIri,
        }).map(({ iri, label }) => [iri, label])
      );
      setLabels((prev) => ({
        ...prev,
        labelsByIri: {
          ...prev.labelsByIri,
          ...newLabelsByIri,
        },
      }));
    }

    if (
      (didCurrentIriChange ||
        didHasCurrentIriDataChange ||
        didSettingsChange ||
        didModelChange ||
        didModelsLoadingChange) &&
      currentIri &&
      databaseId &&
      model &&
      !modelsLoading
    ) {
      // do not refetch loaded data
      if (hasCurrentIriData && !didSettingsChange) {
        return;
      }

      fetchDataForIri(currentIri);
    }
  }, [
    currentIri,
    databaseId,
    didCurrentIriChange,
    didHasCurrentIriDataChange,
    didModelChange,
    didModelsLoadingChange,
    didSettingsChange,
    fetchDataForIri,
    hasCurrentIriData,
    model,
    modelsLoading,
  ]);

  React.useEffect(() => {
    // clear analysis and chart data when conversation changes
    if (didConversationIdChange) {
      setAnalysisDataByMessageId({});
      setChartDataByMessageId({});
    }
  }, [didConversationIdChange]);

  const { requestAnalysis, setAnalysis } = useRequestAnalysis({
    copy,
    connectionId,
    connectionIdx,
    conversationId,
    messages,
    setAnalysisDataByMessageId,
  });

  const { requestChart, setChartCustomizations } = useRequestChart({
    copy,
    chartDataByMessageId,
    connectionId,
    connectionIdx,
    conversationId,
    messages,
    setChartDataByMessageId,
  });

  return {
    currentPanel,
    panelHistory,
    addPanelHistory: React.useCallback((panel: VoiceboxPanelHistory) => {
      setPanelHistory((prev) => {
        // istanbul ignore if
        if (!prev) {
          return [panel];
        }

        const last = prev[prev.length - 1];
        return isEqual(panel, last)
          ? /* istanbul ignore next */ prev
          : [...prev, panel];
      });
    }, []),
    setPanelHistory,

    analysisDataByMessageId,
    requestAnalysis,
    setAnalysis,

    chartDataByMessageId,
    requestChart,
    setChartCustomizations,

    dataByIri,
    getLabelOrLocalName,
    labels,
    provenance,
  };
};
